import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from '../../hooks/useTranslation';
import InventoryDataService from '../../services/inventory.service';
import ProductDataService from '../../services/product.service';
import LocationDataService from '../../services/location.service';
import OriginDataService from '../../services/origin.service';
import ProviderDataService from '../../services/provider.service';
import { formatCurrencySync, initializeCurrency } from '../../utils/currencyUtils';

const InventoryForm = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [providers, setProviders] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [availableInventory, setAvailableInventory] = useState(null);
  const [formData, setFormData] = useState({
    product_id: '',
    location_id: '',
    type: 'buy',
    quantity: '',
    unit_price: '',
    origin_id: '',
    provider_id: '',
    buy_order_attachment: null,
    notes: '',
    transaction_date: dayjs(),
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const defaultType = searchParams.get('type') || 'buy';
  const { t,language } = useTranslation();

  useEffect(() => {
    fetchProducts();
    fetchLocations();
    fetchOrigins();
    fetchProviders();
    initializeCurrency().then(() => {
      const { getCurrencySymbolSync } = require('../../utils/currencyUtils');
      setCurrencySymbol(getCurrencySymbolSync());
    });
    if (!isEdit) {
      setFormData(prev => ({ ...prev, type: defaultType }));
    }
    if (isEdit) {
      fetchInventory();
    }
  }, [id, isEdit, defaultType]);

  const fetchProducts = async () => {
    try {
      const response = await ProductDataService.getAll();
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await LocationDataService.getAll();
      setLocations(response.data?.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  const fetchOrigins = async () => {
    try {
      const response = await OriginDataService.getActive();
      setOrigins(response.data?.data || []);
    } catch (error) {
      console.error('Error loading origins:', error);
      setOrigins([]);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await ProviderDataService.getActive();
      setProviders(response.data?.data || []);
    } catch (error) {
      console.error('Error loading providers:', error);
      setProviders([]);
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const response = await InventoryDataService.get(id);
      const inventory = response.data?.data;
      setFormData({
        product_id: inventory.product_id || '',
        location_id: inventory.location_id || '',
        type: inventory.type || 'buy',
        quantity: inventory.quantity != null ? String(inventory.quantity) : '',
        unit_price: inventory.unit_price != null ? String(inventory.unit_price) : '',
        origin_id: inventory.origin_id || '',
        provider_id: inventory.provider_id || '',
        buy_order_attachment: null,
        notes: inventory.notes || '',
        transaction_date: inventory.transaction_date 
          ? dayjs(inventory.transaction_date) 
          : dayjs(inventory.created_at),
      });
      
      // Fetch available inventory if editing a sell transaction
      if (inventory.type === 'sell' && inventory.product_id) {
        fetchAvailableInventory(inventory.product_id);
      }
    } catch (error) {
      console.error('Error loading inventory:', error);
      showSnackbar(t('inventories.errorLoadingTransaction'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableInventory = async (productId, type = null) => {
    const transactionType = type || formData.type;
    if (!productId || transactionType !== 'sell') {
      setAvailableInventory(null);
      return;
    }

    try {
      const response = await InventoryDataService.getAll({ product_id: productId });
      const inventories = response.data?.data?.data || [];
      
      // Sum remaining_inventory from all buy transactions
      const available = inventories
        .filter(inv => inv.type === 'buy' && inv.remaining_inventory !== undefined)
        .reduce((sum, inv) => sum + parseFloat(inv.remaining_inventory || 0), 0);
      
      setAvailableInventory(available);
    } catch (error) {
      console.error('Error fetching available inventory:', error);
      setAvailableInventory(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For quantity and unit_price, preserve the exact value as string to avoid floating point issues
    if (name === 'quantity' || name === 'unit_price') {
      // Remove any non-numeric characters except decimal point
      const cleanedValue = value.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = cleanedValue.split('.');
      const finalValue = parts.length > 2 
        ? parts[0] + '.' + parts.slice(1).join('')
        : cleanedValue;
      setFormData(prev => ({ ...prev, [name]: finalValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // If product changed and it's a sell transaction, fetch available inventory
    if (name === 'product_id' && formData.type === 'sell') {
      fetchAvailableInventory(value, formData.type);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, buy_order_attachment: file }));
    }
  };

  const handleSubmit = async () => {
    // Validate and clean form data
    const quantityStr = String(formData.quantity || '').trim();
    const unitPriceStr = String(formData.unit_price || '').trim();
    
    // Validate that strings are not empty and are valid numbers
    if (!quantityStr || quantityStr === '' || quantityStr === '.') {
      showSnackbar(t('inventories.quantityRequired'), 'error');
      return;
    }
    
    if (!unitPriceStr || unitPriceStr === '' || unitPriceStr === '.') {
      showSnackbar(t('inventories.unitPriceRequired'), 'error');
      return;
    }
    
    const quantity = parseFloat(quantityStr);
    const unitPrice = parseFloat(unitPriceStr);
    
    if (isNaN(quantity) || quantity <= 0) {
      showSnackbar(t('inventories.quantityMustBeValid'), 'error');
      return;
    }
    
    if (isNaN(unitPrice) || unitPrice < 0) {
      showSnackbar(t('inventories.unitPriceMustBeValid'), 'error');
      return;
    }
    
    if (!formData.product_id) {
      showSnackbar(t('inventories.productRequired'), 'error');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        quantity: quantityStr, // Send as string, service will parse it
        unit_price: unitPriceStr, // Send as string, service will parse it
        transaction_date: formData.transaction_date ? formData.transaction_date.format('YYYY-MM-DD') : null,
      };
      
      console.log('Submitting data:', submitData);
      console.log('Quantity value:', submitData.quantity, 'Type:', typeof submitData.quantity);

      if (isEdit) {
        await InventoryDataService.update(id, submitData);
        showSnackbar(t('inventories.transactionUpdatedSuccess'));
      } else {
        await InventoryDataService.create(submitData);
        showSnackbar(t('inventories.transactionCreatedSuccess'));
      }
      navigate('/inventories');
    } catch (error) {
      let errorMessage = `Error ${isEdit ? 'updating' : 'creating'} transaction`;
      
      // Check for inventory validation error
      if (error.response?.data?.errors?.quantity) {
        const quantityError = Array.isArray(error.response.data.errors.quantity) 
          ? error.response.data.errors.quantity[0] 
          : error.response.data.errors.quantity;
        errorMessage = quantityError;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        if (error.response.data.available_inventory !== undefined) {
          errorMessage += `. Available inventory: ${parseFloat(error.response.data.available_inventory).toFixed(2)}`;
        }
      }
      
      showSnackbar(errorMessage, 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const total = formData.quantity && formData.unit_price 
    ? formatCurrencySync(parseFloat(formData.quantity) * parseFloat(formData.unit_price))
    : formatCurrencySync(0);

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate('/inventories')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">
                {isEdit ? 'Edit Transaction' : (t('inventories.New')  + ' ' + (formData.type === 'buy' ? t('inventories.Buy') : t('inventories.Sell')))}
              </Typography>
              {!isEdit && (
                <Chip
                  label={formData.type.toUpperCase()}
                  color={formData.type === 'buy' ? 'success' : 'error'}
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
          }
          action={
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={loading}
              color={formData.type === 'buy' ? 'success' : 'error'}
            >
              {isEdit ? t('common.update') : t('common.save')} {t('inventories.Transaction')}
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>{t('inventories.Type')}</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={(e) => {
                    handleInputChange(e);
                    // Fetch available inventory when switching to sell
                    if (e.target.value === 'sell' && formData.product_id) {
                      fetchAvailableInventory(formData.product_id, e.target.value);
                    } else {
                      setAvailableInventory(null);
                    }
                  }}
                  label="Transaction Type"
                  disabled={isEdit}
                >
                  <MenuItem value="buy">{t('inventories.Buy')}</MenuItem>
                  <MenuItem value="sell">{t('inventories.Sell')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label={t('inventories.Date')}
                value={formData.transaction_date}
                onChange={(newValue) => setFormData({ ...formData, transaction_date: newValue })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>{t('inventories.Product')}</InputLabel>
                <Select
                  name="product_id"
                  value={formData.product_id}
                  onChange={(e) => {
                    handleInputChange(e);
                    // Fetch available inventory when product is selected for sell transactions
                    if (formData.type === 'sell' && e.target.value) {
                      fetchAvailableInventory(e.target.value);
                    } else {
                      setAvailableInventory(null);
                    }
                  }}
                  label="Product"
                >
                  <MenuItem value="">{t('inventories.SelectProduct')}</MenuItem>
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('inventories.Location')}</InputLabel>
                <Select
                  name="location_id"
                  value={formData.location_id}
                  onChange={handleInputChange}
                  label="Location"
                >
                  <MenuItem value="">{t('inventories.NoLocation')}</MenuItem>
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('inventories.Quantity')}
                name="quantity"
                type="text"
                value={formData.quantity || ''}
                onChange={handleInputChange}
                inputProps={{ 
                  inputMode: 'decimal',
                  pattern: '[0-9]*\\.?[0-9]*'
                }}
                required
                error={formData.type === 'sell' && availableInventory !== null && parseFloat(formData.quantity || 0) > availableInventory}
                helperText={
                  formData.type === 'sell' && availableInventory !== null
                    ? `${t('inventories.AvailableInventory') || 'Available Inventory'}: ${availableInventory.toFixed(2)}${parseFloat(formData.quantity || 0) > availableInventory ? ` - ${t('inventories.InsufficientInventory') || 'Insufficient inventory'}` : ''}`
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('inventories.UnitPrice')}
                name="unit_price"
                type="text"
                value={formData.unit_price || ''}
                onChange={handleInputChange}
                inputProps={{ 
                  inputMode: 'decimal',
                  pattern: '[0-9]*\\.?[0-9]*'
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('inventories.Total')}:
                </Typography>
                <Typography variant="h6" color="primary">
                  {total}
                </Typography>
              </Box>
            </Grid>
            {formData.type === 'buy' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('inventories.Provider')}</InputLabel>
                  <Select
                    name="provider_id"
                    value={formData.provider_id}
                    onChange={handleInputChange}
                    label={t('inventories.Provider')}
                  >
                    <MenuItem value="">{t('inventories.NoProvider')}</MenuItem>
                    {providers.map(provider => (
                      <MenuItem key={provider.id} value={provider.id}>
                        {provider.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {formData.type === 'sell' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('inventories.Origin')}</InputLabel>
                  <Select
                    name="origin_id"
                    value={formData.origin_id}
                    onChange={handleInputChange}
                    label={t('inventories.Origin')}
                  >
                    <MenuItem value="">{t('inventories.NoOrigin')}</MenuItem>
                    {origins.map(origin => (
                      <MenuItem key={origin.id} value={origin.id}>
                        {origin.name || (language === 'es' ? origin.name_es : origin.name_en)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="file"
                label="Buy Order Attachment"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: 'application/pdf,.doc,.docx,image/*' }}
                onChange={handleFileChange}
                helperText="Upload PDF, DOC, DOCX, or image files (max 5MB)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('inventories.Notes')}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
                placeholder="Additional notes or comments"
              />
            </Grid>
          </Grid>
        </CardContent>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Card>
    </LocalizationProvider>
  );
};

export default InventoryForm;
